import {
  electoralListChooserCandidateSuffix,
  electoralListChooserColumnsSuffix,
  electoralListChooserListNameSuffix,
  FormBuilderMetaFlags,
  type ICreateWidgetFormSchemaInput,
} from '@framework/engine';
import { yup } from '@framework/validation';
import { fromPairs } from 'lodash';
import { getDatatypeColumnSchema } from '../candidate/getDatatypeColumnSchema';
import { getCandidateColumns } from '../candidate/table/CandidateColumns';
import type { IElectoralListChooserWidget } from './IElectoralListChooserWidget';

/** Nome del sotto-campo che contiene il candidato scelto nella lista selezionata. */
export function electoralListChooserCandidateFieldName(code: string) {
  return `${code}${electoralListChooserCandidateSuffix}`;
}

/** Nome del sotto-oggetto con i campi (colonne) del candidato scelto. */
export function electoralListChooserColumnsFieldName(code: string) {
  return `${code}${electoralListChooserColumnsSuffix}`;
}

/** Nome del sotto-campo con il nome della lista selezionata. */
export function electoralListChooserListNameFieldName(code: string) {
  return `${code}${electoralListChooserListNameSuffix}`;
}

export function createElectoralListChooserFormSchema(
  input: ICreateWidgetFormSchemaInput<IElectoralListChooserWidget>,
) {
  const { widget } = input;

  // Il valore del widget è l'id della lista elettorale selezionata.
  const selectedListSchema = yup
    .string()
    .notRequired()
    .label('ID Lista Selezionata');

  // Candidato scelto all'interno della lista selezionata: opzionale. Le regole
  // di validazione del widget (es. Required) non devono ricadere su questo
  // sotto-campo, quindi lo marchiamo perché il FormBuilder salti regole e valore
  // di default, come fa createCandidateSelectFormSchema.
  const candidateSchema = yup
    .string()
    .notRequired()
    .label('ID Candidato Selezionato')
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipValidationRules,
    );

  // Campi del candidato scelto, salvati come oggetto così che le espressioni
  // possano referenziarli (nome, cognome, …) — come fa il widget CTS con
  // `@columns`. Solo le colonne configurate, tipizzate; niente regole né default.
  const columnsSchema = yup
    .object(
      fromPairs(
        getCandidateColumns(widget.columns).map(column => [
          column.name,
          getDatatypeColumnSchema(column.type),
        ]),
      ),
    )
    .notRequired()
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipValidationRules,
    );

  // Nome della lista selezionata, salvato alla selezione così che le espressioni
  // possano referenziarlo (#57760); niente regole né default, come il candidato.
  const listNameSchema = yup
    .string()
    .notRequired()
    .label('Nome Lista Selezionata')
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipValidationRules,
    );

  return {
    [widget.code]: selectedListSchema,
    [electoralListChooserCandidateFieldName(widget.code)]: candidateSchema,
    [electoralListChooserColumnsFieldName(widget.code)]: columnsSchema,
    [electoralListChooserListNameFieldName(widget.code)]: listNameSchema,
  };
}
